import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, can, type Role, type UserRow } from "../api";
import { useMe } from "../App";
import { Person, relative } from "../components/ui";

const ROLES: { role: Role; meaning: string }[] = [
  { role: "viewer", meaning: "Sees projects, environments and who owns them" },
  { role: "dev", meaning: "Creates projects and demos, ships what they own" },
  { role: "admin", meaning: "Approves, manages people, reads the audit log" },
];

export function People() {
  const { data: me } = useMe();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: () => api<UserRow[]>("/users") });
  const setRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: Role }) => api(`/users/${id}/role`, { method: "PATCH", body: JSON.stringify({ role }) }),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["users"] }); qc.invalidateQueries({ queryKey: ["me"] }); },
  });
  const admin = can(me, "admin");
  const admins = users.data?.filter((u) => u.role === "admin").length ?? 0;

  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>People</h1>
          <p className="sub">{users.data ? `${users.data.length} people have signed in · ${admins} admin${admins === 1 ? "" : "s"}` : "Everyone who has signed in with Bitbucket."}</p>
        </div>
      </div>
      <div className="split">
        <section className="panel flush" aria-label="People">
          {setRole.error && <div className="item"><span className="error">{setRole.error.message}</span></div>}
          <div className="table-wrap">
            <table>
              <thead><tr><th>Person</th><th>Role</th><th className="num hide-phone">Last sign-in</th></tr></thead>
              <tbody>
                {users.data?.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <Person name={u.displayName} avatarUrl={u.avatarUrl} />
                        <span>
                          <span className="cell-main">{u.displayName}</span>
                          <span className="cell-sub" style={{ display: "block" }}>{u.username}{u.id === me?.id ? " · you" : ""}</span>
                        </span>
                      </span>
                    </td>
                    <td>
                      {admin ? (
                        <select className="input" style={{ height: 32, width: 120 }} aria-label={`Role for ${u.username}`} value={u.role}
                          disabled={setRole.isPending} onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value as Role })}>
                          {ROLES.map((r) => <option key={r.role}>{r.role}</option>)}
                        </select>
                      ) : <span className="pill">{u.role}</span>}
                    </td>
                    <td className="num muted hide-phone">{relative(u.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <aside className="panel" aria-labelledby="roles">
          <h2 id="roles" style={{ marginBottom: 12 }}>Roles</h2>
          <dl className="kv" style={{ gridTemplateColumns: "64px minmax(0, 1fr)" }}>
            {ROLES.map((r) => <><dt key={`t-${r.role}`} className="mono">{r.role}</dt><dd key={`d-${r.role}`}>{r.meaning}</dd></>)}
          </dl>
          <p className="muted" style={{ marginTop: 16 }}>Anyone in the 525k Bitbucket workspace can sign in and starts as dev. The last admin can't be demoted.</p>
        </aside>
      </div>
    </div>
  );
}
